import { useEffect, useState } from 'react';

// iOS 13+ Safari requires an explicit permission grant, requested from a user gesture.
// Android/Chrome and desktop browsers don't gate the API.
export async function requestHeadingPermission(): Promise<boolean> {
	const DOE = (window as any).DeviceOrientationEvent;
	if (DOE && typeof DOE.requestPermission === 'function') {
		try {
			const result = await DOE.requestPermission();
			return result === 'granted';
		} catch {
			return false;
		}
	}
	return true;
}

// Shortest signed delta between two compass headings, in [-180, 180].
// e.g. shortestDelta(355, 5) === 10, shortestDelta(5, 355) === -10
function shortestDelta(from: number, to: number): number {
	let d = to - from;
	if (d > 180) d -= 360;
	if (d < -180) d += 360;
	return d;
}

interface UseDeviceHeadingOptions {
	// EMA factor in (0, 1]. Lower = smoother but laggier. 0.15 is a good default for a phone compass.
	smoothing?: number;
	// Minimum change (in degrees) from the last emitted value before a new state update fires.
	// Prevents sub-perceptual map re-renders. 2-3° is a reasonable floor.
	thresholdDeg?: number;
}

// Returns the device's compass heading in degrees [0, 360) when `active`, null otherwise.
// Applies EMA smoothing + a minimum-delta threshold to suppress sensor jitter.
export function useDeviceHeading(active: boolean, options: UseDeviceHeadingOptions = {}): number | null {
	const { smoothing = 0.15, thresholdDeg = 1.5 } = options;
	const [heading, setHeading] = useState<number | null>(null);

	useEffect(() => {
		if (!active) {
			setHeading(null);
			return;
		}

		let raf = 0;
		let smoothed: number | null = null;
		let lastEmitted: number | null = null;

		const handler = (e: DeviceOrientationEvent) => {
			let raw: number | null = null;

			const webkitHeading = (e as any).webkitCompassHeading;
			if (typeof webkitHeading === 'number') {
				// iOS — already a true compass heading (0 = north)
				raw = webkitHeading;
			} else if (e.alpha !== null && e.alpha !== undefined) {
				// Android/Chrome — alpha is z-axis rotation, increases counter-clockwise from north
				raw = 360 - e.alpha;
			}

			if (raw === null) return;

			// Compensate for screen orientation (landscape rotation, etc.)
			const screenAngle = (window.screen?.orientation?.angle ?? (window as any).orientation ?? 0) as number;
			raw = (raw + screenAngle) % 360;
			if (raw < 0) raw += 360;

			// EMA blend toward the raw reading along the shortest arc
			if (smoothed === null) {
				smoothed = raw;
			} else {
				const delta = shortestDelta(smoothed, raw);
				smoothed = (smoothed + smoothing * delta + 360) % 360;
			}

			// Only emit if smoothed value moved past the threshold from last emitted
			const candidate = smoothed;
			if (lastEmitted === null || Math.abs(shortestDelta(lastEmitted, candidate)) >= thresholdDeg) {
				if (!raf) {
					raf = requestAnimationFrame(() => {
						raf = 0;
						lastEmitted = candidate;
						setHeading(candidate);
					});
				}
			}
		};

		// `deviceorientationabsolute` is a true compass on browsers that support it; fall back to
		// the relative event (only iOS Safari uses webkitCompassHeading there).
		const supportsAbsolute = 'ondeviceorientationabsolute' in window;
		const eventName: 'deviceorientationabsolute' | 'deviceorientation' =
			supportsAbsolute ? 'deviceorientationabsolute' : 'deviceorientation';
		window.addEventListener(eventName, handler as EventListener);

		return () => {
			window.removeEventListener(eventName, handler as EventListener);
			if (raf) cancelAnimationFrame(raf);
		};
	}, [active, smoothing, thresholdDeg]);

	return heading;
}
